import React from 'react';
import { Divider, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { adminFee, premiumPricingDiff, lunchPenalty } from 'app/models/Placement/PlacementMods';
import { useSelector } from 'react-redux';
import { selectPricingReason } from 'store/redux-store/placement-mods/selectors';
import { validDecimal } from 'app/helpers/numberHelper';
import { FormInputDropdown } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDropdown';

export const PricingAndFees = ({ classes, isModEdit }: { classes: any; isModEdit: boolean }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const pricingReasons = useSelector(selectPricingReason);

  return (
    <Grid style={{ marginTop: '24px' }}>
      <Grid className={classes.divider}>
        <Divider />
      </Grid>
      <div style={{ marginTop: '24px' }}>
        <Typography component="span" className={classes.headings}>{`${t(
          'placement.placementMods.pricingFees',
        )}`}</Typography>
      </div>
      <Grid className={classes.flex}>
        <Controller
          name={adminFee}
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              value={getValues(adminFee)}
              className={classes.pricingTextfields}
              InputLabelProps={{
                shrink: getValues(adminFee),
              }}
              variant="filled"
              label={`${t(`placement.placementMods.${adminFee}`)}`}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setValue(adminFee, e.target.value);
                  onChange(e.target.value);
                }
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit ? 'auto' : 'none',
                },
                startAdornment: (
                  <>{getValues(adminFee) && <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>}</>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
        <Grid className={classes.factorMarginBillRate}>
          <Controller
            control={control}
            name={'amountPerMile'}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                variant="filled"
                fullWidth
                className={classes.pricingTextfields}
                color="primary"
                autoComplete="off"
                label={t('placement.placementMods.amountPerMile')}
                onChange={e => {
                  const validInput = e.target.value.replace(/^\./, '0.');
                  if (
                    validInput === '' ||
                    validDecimal(validInput, { numberLength: 6, decimalLength: 3 })
                  ) {
                    const valueToSet =
                      validInput === '' || validInput.endsWith('.')
                        ? validInput
                        : parseFloat(validInput);
                    setValue('amountPerMile', valueToSet);
                    onChange(valueToSet);
                  }
                }}
                InputLabelProps={{
                  shrink: getValues('amountPerMile'),
                }}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues('amountPerMile') && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                }}
                value={getValues('amountPerMile') ?? ''}
                required={false}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMarginBillRate}>
          <Controller
            name={premiumPricingDiff}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={getValues(premiumPricingDiff)}
                variant="filled"
                className={classes.pricingTextfields}
                label={`${t(`placement.placementMods.${premiumPricingDiff}`)}`}
                InputLabelProps={{
                  shrink: getValues(premiumPricingDiff),
                }}
                onChange={e => {
                  if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                    setValue(premiumPricingDiff, e.target.value);
                    onChange(e.target.value);
                  }
                }}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues(premiumPricingDiff) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMarginBillRate}>
          <Controller
            control={control}
            name={`premiumPricingReasons`}
            render={({ field: { onChange, value, ...rest } }) => {
              return (
                <FormInputDropdown
                  inputValue={getValues('premiumPricingReasons') ?? []}
                  name={`premiumPricingReasons`}
                  label={t('placement.placementMods.reasons')}
                  customClass={classes.pricingTextfields}
                  standard={true}
                  useMenuItem
                  multiple
                  options={pricingReasons?.length > 0 ? pricingReasons : []}
                  handleChange={e => {
                    onChange(e);
                  }}
                  readOnly={!isModEdit}
                ></FormInputDropdown>
              );
            }}
          />
        </Grid>
        <Grid className={classes.factorMarginBillRate}>
          <Controller
            name={lunchPenalty}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={getValues(lunchPenalty)}
                className={classes.pricingTextfields}
                InputLabelProps={{
                  shrink: getValues(lunchPenalty) ?? false,
                }}
                onChange={e => {
                  if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                    setValue(lunchPenalty, e.target.value);
                    onChange(e.target.value);
                  }
                }}
                variant="filled"
                label={`${t(`placement.placementMods.${lunchPenalty}`)}`}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues(lunchPenalty) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
